import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Paperclip, MessageSquare } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const formatShortValue = (value) => {
  if (value === undefined || value === null || value === '') return '';
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  let formatted = '';
  if (num >= 1.0e9) {
    formatted = (num / 1.0e9).toFixed(2) + 'B';
  } else if (num >= 1.0e6) {
    formatted = (num / 1.0e6).toFixed(2) + 'M';
  } else if (num >= 1.0e3) {
    formatted = (num / 1.0e3).toFixed(2) + 'K';
  } else {
    formatted = num.toFixed(2);
  }
  return formatted.replace(/\.00(?=[KMB]?$)/, '');
};

export default function OpportunityCard({ opportunity, onClick, isReadOnly, isDraggingOverlay }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: opportunity.id,
    disabled: isReadOnly || isDraggingOverlay,
    data: { type: 'Opportunity', opportunity }
  });

  const style = isDragging ? {
    opacity: 0.3,
    position: 'relative'
  } : isDraggingOverlay ? {
    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5)',
    transform: 'rotate(2deg)',
    cursor: 'grabbing'
  } : undefined;

  const notesCount = opportunity.notes?.length || 0;
  const attachmentsCount = opportunity.attachments?.length || 0;

  let dateStr = '';
  if (opportunity.dateAdded) {
    try {
      dateStr = format(parseISO(opportunity.dateAdded), 'MMM d, yyyy');
    } catch (e) {
      dateStr = opportunity.dateAdded;
    }
  }

  return (
    <div 
      ref={setNodeRef} 
      className="card glass-card"
      style={{
        padding: '1rem',
        margin: '0.5rem 0',
        cursor: isDraggingOverlay ? 'grabbing' : (isReadOnly ? 'pointer' : 'grab'),
        boxShadow: isDraggingOverlay 
          ? '0 10px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5)'
          : (isDragging ? 'var(--shadow-lg)' : 'var(--shadow-sm)'),
        ...style
      }}
      {...(isReadOnly ? {} : listeners)}
      {...(isReadOnly ? {} : attributes)}
      onClick={() => onClick(opportunity)}
    >
      <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
        {opportunity.shortDesc || opportunity.name || <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>No Name</span>}
      </div>
      
      {opportunity.description && (
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: '0.5rem', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {opportunity.description}
        </div>
      )}

      {opportunity.estimatedValue && (
        <div style={{ fontSize: '0.8125rem', color: 'var(--primary)', fontWeight: 600, marginBottom: '0.35rem' }}>
          Est. Value: ${formatShortValue(opportunity.estimatedValue)}
        </div>
      )}

      {opportunity.assignedTo && (
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.35rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <span style={{ color: 'var(--text-muted)' }}>Lead:</span>
          <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{opportunity.assignedTo}</span>
        </div>
      )}

      {opportunity.jurisdiction && (
        <div style={{ marginBottom: '0.4rem' }}>
          <span style={{ 
            fontSize: '0.65rem', 
            textTransform: 'uppercase', 
            letterSpacing: '0.05em',
            fontWeight: 700,
            padding: '0.1rem 0.45rem', 
            borderRadius: '4px',
            backgroundColor: opportunity.jurisdiction === 'Federal' ? 'rgba(59, 130, 246, 0.15)' : opportunity.jurisdiction === 'State' ? 'rgba(168, 85, 247, 0.15)' : 'rgba(234, 179, 8, 0.15)',
            color: opportunity.jurisdiction === 'Federal' ? '#60A5FA' : opportunity.jurisdiction === 'State' ? '#C084FC' : '#FBBF24',
            border: `1px solid ${opportunity.jurisdiction === 'Federal' ? 'rgba(59, 130, 246, 0.3)' : opportunity.jurisdiction === 'State' ? 'rgba(168, 85, 247, 0.3)' : 'rgba(234, 179, 8, 0.3)'}`,
            display: 'inline-block'
          }}>
            {opportunity.jurisdiction}
          </span>
        </div>
      )}

      {opportunity.agencies && opportunity.agencies.filter(Boolean).length > 0 && (
        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
          {opportunity.agencies.filter(Boolean).map((agency, idx) => (
            <span key={idx} style={{ fontSize: '0.7rem', backgroundColor: 'rgba(0, 224, 150, 0.1)', color: '#00e096', border: '1px solid rgba(0, 224, 150, 0.2)', padding: '0.1rem 0.35rem', borderRadius: '4px' }}>
              {agency}
            </span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75rem', borderTop: '1px solid var(--border-color)', paddingTop: '0.5rem' }}>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
          {dateStr && `Added: ${dateStr}`}
        </span>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', color: 'var(--text-secondary)' }}>
          {attachmentsCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.75rem' }} title={`${attachmentsCount} attachments`}>
              <Paperclip size={12} /> {attachmentsCount}
            </span>
          )}
          {notesCount > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.75rem' }} title={`${notesCount} notes`}>
              <MessageSquare size={12} /> {notesCount}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
